/**
 * Shared logic for the inline alert rule create/edit form.
 * Import and spread into alertCreatePage.
 */
export const alertFormMixin = {
    emails: [],
    emailError: '',

    setEmailError(msg) {
        this.emailError = msg;
        const el = document.getElementById('emailTo-error');
        if (!el) return;
        if (msg) {
            el.textContent = msg;
            el.classList.remove('hidden');
        } else {
            el.textContent = '';
            el.classList.add('hidden');
        }
    },
    editingId: null,

    form: {
        name: '',
        eventType: 'browser_stop_failed',
        conditionType: 'consecutive',
        threshold: 3,
        windowMinutes: '',
    },

    resetForm() {
        this.editingId = null;
        this.emails = [];
        this.setEmailError('');
        if (this.$refs.emailEditable) this.$refs.emailEditable.textContent = '';
        this.form = {
            name: '',
            eventType: 'browser_stop_failed',
            conditionType: 'consecutive',
            threshold: 3,
            windowMinutes: '',
        };
    },

    startEdit(rule) {
        const data = typeof rule === 'string' ? JSON.parse(rule) : rule;
        this.editingId = data.alertRuleId;
        this.emails = this.parseEmails(data.emailTo);
        this.setEmailError('');
        if (this.$refs.emailEditable) this.$refs.emailEditable.textContent = '';
        this.form = {
            name: data.name,
            eventType: data.eventType,
            conditionType: data.conditionType,
            threshold: Number(data.threshold),
            windowMinutes: data.windowMinutes ? Number(data.windowMinutes) : '',
        };
        // keep the card in place; just scroll it to the center of the screen
        this.scrollToCard(this.editingId);
    },

    scrollToCard(id) {
        if (!id) return;
        // wait a tick so the list re-render has settled
        requestAnimationFrame(() => {
            const card = document.querySelector(`#alert-rules-list [data-rule-id="${id}"]`);
            if (card) card.scrollIntoView({behavior: 'smooth', block: 'center'});
        });
    },

    updateCardOptimistically(id, form, emails) {
        if (!id) return;
        const card = document.querySelector(`#alert-rules-list [data-rule-id="${id}"]`);
        if (!card) return;

        const name = card.querySelector('[data-card-name]');
        if (name) name.textContent = form.name;

        const eventLabel = form.eventType === 'max_browsers_reached'
            ? 'Max active browsers reached'
            : 'Browser Stop returning failure';
        const conditionLabel = form.conditionType === 'consecutive'
            ? `${form.threshold} time(s) in a row`
            : `${form.threshold} time(s) within ${form.windowMinutes} minutes`;

        const summary = card.querySelector('[data-card-summary]');
        if (summary) summary.textContent = `${eventLabel} — ${conditionLabel}`;

        const emailList = card.querySelector('[data-card-emails]');
        if (!emailList) return;
        emailList.innerHTML = '<i class="ti ti-mail text-base-content/50 text-xs"></i>';
        for (const email of emails) {
            const badge = document.createElement('span');
            badge.className = 'badge badge-ghost badge-sm text-base-content/60 border border-base-content/20';
            badge.textContent = email;
            emailList.appendChild(badge);
        }
    },

    async onSaveSuccess() {
        if (this._reloadingList) return;

        const id = this.editingId;
        const form = {...this.form};
        const emails = [...this.emails];

        this.updateCardOptimistically(id, form, emails);
        this.resetForm();

        if (this.$ajax) {
            this._reloadingList = true;
            try {
                await this.$ajax('/alerts', {
                    target: 'alert-rules-list',
                    sync: false,
                    focus: false,
                });
            } finally {
                this._reloadingList = false;
            }
        } else {
            this.$refs.reloadLink?.click();
        }

        this.scrollToCard(id);
    },

    cancelEdit() {
        const id = this.editingId;
        this.resetForm();
        this.scrollToCard(id);
    },

    onRuleDeleted(id) {
        // if the rule being edited was just deleted, drop edit mode and clear the form
        if (this.editingId === id) {
            this.resetForm();
        }
    },

    async openHistory(alertRuleId) {
        const modal = document.getElementById('alert-history-modal');
        const content = document.getElementById('alert-history-content');
        content.innerHTML = '<div class="flex justify-center py-10"><span class="loading loading-spinner loading-md"></span></div>';
        modal.classList.add('modal-open');
        try {
            const response = await fetch('/alerts/' + alertRuleId + '/history', {
                headers: {'X-Alpine-Request': 'true'},
            });
            if (!response.ok) throw new Error('failed');
            content.innerHTML = await response.text();
        } catch {
            content.innerHTML = '<p class="text-center text-error py-10">Failed to load history.</p>';
        }
    },

    get maxWindow() {
        return this.form.eventType === 'max_browsers_reached' ? 60 : 10080;
    },

    get windowExceeded() {
        return this.form.windowMinutes !== '' && Number(this.form.windowMinutes) > this.maxWindow;
    },

    clampWindow() {
        if (this.windowExceeded) this.form.windowMinutes = this.maxWindow;
    },

    isValidEmail(email) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    },

    pushEmail(value) {
        this.setEmailError('');
        if (value === '') return;
        if (this.emails.length >= 10) {
            this.setEmailError('Maximum of 10 email addresses allowed.');
            return;
        }
        if (!this.isValidEmail(value)) {
            this.setEmailError(`"${value}" is not a valid email.`);
            return;
        }
        if (this.emails.includes(value)) {
            this.setEmailError(`"${value}" was already added.`);
            return;
        }
        this.emails.push(value);
    },

    addEmailFromEditable() {
        const el = this.$refs.emailEditable;
        if (!el) return;
        const value = el.textContent.trim().replace(/,$/, '').trim();
        el.textContent = '';
        this.pushEmail(value);
    },

    removeEmail(idx) {
        this.emails.splice(idx, 1);
        this.setEmailError('');
    },

    onEmailBackspace() {
        if (this.emails.length) this.removeEmail(this.emails.length - 1);
    },

    parseEmails(str) {
        if (!str) return [];
        const result = [];
        for (const part of str.split(',')) {
            const email = part.trim();
            if (email !== '' && !result.includes(email)) result.push(email);
        }
        return result;
    },
};
